import { getDefaultZIndex, Group } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	apiGetBiDashboardQueryKey,
	type BiDashboardSecodaEntityOut,
} from '@repo/api-codegen';
import { UserRole } from '@repo/common/enums/UserRole';
import { Breadcrumbs, Button, Icon, IconButton, Text } from '@repo/foundations';
import { useQueryClient } from '@tanstack/react-query';
import { every, includes, isString } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useUpdateSecodaEntity, type IUser } from '../../api';
import { resourceCatalogQueryKeyFactory } from '../../api/hooks/resourceCatalog/constants';
import { useDefaultTeam } from '../../api/hooks/team/myMemberships';
import { openDeleteConfirmModal } from '../../components/ModalManager/events';
import type {
	RightSidebarMode,
	RightSidebarStore,
} from '../../components/RightSidebar/store';
import {
	NavBarBreadcrumbWrapper,
	NavBarRightSideWrapper,
	NavBarSidesheetControlsWrapper,
	NavBarWrapper,
} from '../../components/SecodaAppShell/SecodaNavBar';
import type { DjangoValueType } from '../../pages/TemplatePage/types';
import { useCheckIAMPermission } from '../../utils/authorization/roles';
import { BiDashboardActions } from './components/BiDashboardActions';
import { useBiDashboardBreadcrumbs } from './hooks';
import { useBiDashboardExport } from './hooks/useBiDashboardExport';
import type { BIDashboardStore } from './store';
import type { BIDashboardMode } from './types';

interface BIDashboardNavBarProps {
	id: string;
	entity: BiDashboardSecodaEntityOut;
	mode: BIDashboardMode;
	user: IUser;
	dashboardRightSidebarStore: RightSidebarStore;
	dashboardStore: BIDashboardStore;
	onAddMetric: () => void;
	onCloseCanvas?: () => void;
}

export const BiDashboardNavBarController = observer(
	({
		id,
		entity,
		mode,
		user,
		dashboardRightSidebarStore,
		dashboardStore,
		onAddMetric,
		onCloseCanvas,
	}: BIDashboardNavBarProps) => {
		const navigate = useNavigate();
		const queryClient = useQueryClient();

		const { defaultTeam } = useDefaultTeam();
		const { breadcrumbs, team } = useBiDashboardBreadcrumbs({
			dashboardId: id,
		});

		const { hasPermission: hasManagePermission } = useCheckIAMPermission({
			v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
			v2Permission: 'Dashboards.Manage',
		});

		const { mutateAsync: updateDashboardSecodaEntity } = useUpdateSecodaEntity({
			options: {
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: apiGetBiDashboardQueryKey({
							pathParams: {
								biDashboardId: id,
							},
						}),
					});
				},
			},
		});

		const handleDeleteDashboard = useCallback(async () => {
			openDeleteConfirmModal({
				title: `Delete Bi Dashboard?`,
				description: "You can't undo this action.",
				tone: 'critical',
				confirmProps: {
					variant: 'primary',
					tone: 'critical',
				},
				labels: {
					cancel: 'Cancel',
					confirm: 'Delete Bi Dashboard',
				},
				onConfirm: async () => {
					await updateDashboardSecodaEntity({
						data: {
							id: entity.id,
							archived: true,
						},
					});

					queryClient.invalidateQueries({
						queryKey: apiGetBiDashboardQueryKey({
							pathParams: { biDashboardId: id },
						}),
					});

					queryClient.invalidateQueries({
						queryKey: resourceCatalogQueryKeyFactory.allLists(),
					});

					if (team) {
						navigate(`/teams/${team.id}/bi/dashboards`);
					} else {
						navigate('/bi/dashboards');
					}
				},
			});
		}, [
			entity.id,
			id,
			navigate,
			queryClient,
			team,
			updateDashboardSecodaEntity,
		]);

		const handleToggleBookmark = useCallback(
			(value: DjangoValueType) => {
				if (!Array.isArray(value) || !every(value, isString)) {
					return;
				}

				updateDashboardSecodaEntity({
					data: {
						id: entity.id,
						bookmarked_by: value as string[],
					},
				});
			},
			[entity.id, updateDashboardSecodaEntity]
		);

		const {
			handleCopyAsPng: handleCopyAsPng,
			handleDownloadPng: handleDownloadPng,
		} = useBiDashboardExport({
			dashboardId: id,
			dashboardName: entity.title || 'dashboard',
		});

		const handleToggleRightSidebar = useCallback(
			(
				rightSidebarMode: (typeof RightSidebarMode)[keyof typeof RightSidebarMode]
			) => {
				const currentMode = dashboardRightSidebarStore.mode;
				dashboardRightSidebarStore.setMode(rightSidebarMode);
				if (currentMode === rightSidebarMode) {
					dashboardRightSidebarStore.setCollapsed(
						!dashboardRightSidebarStore.collapsed
					);
				} else if (dashboardRightSidebarStore.collapsed) {
					dashboardRightSidebarStore.setCollapsed(false);
				}
			},
			[dashboardRightSidebarStore]
		);

		const handleGoToDashboard = useCallback(() => {
			navigate(`/bi/dashboards/${id}`);
		}, [id, navigate]);

		const handleSaveDashboard = useCallback(async () => {
			if (defaultTeam?.id) {
				await updateDashboardSecodaEntity({
					data: {
						id: entity.id,
						teams: [defaultTeam?.id],
					},
				});
				showNotification({
					title: 'Dashboard saved',
					message: 'The dashboard has been saved successfully',
					color: 'green',
					icon: <Icon name="check" color="text/success-on-fill/default" />,
				});
			}
		}, [defaultTeam?.id, entity.id, updateDashboardSecodaEntity]);

		const handleRefreshAllMetrics = useCallback(() => {
			dashboardStore.refreshAllMetrics();
		}, [dashboardStore]);

		const isBookmarked = useMemo(
			() => includes(entity.bookmarked_by, user.id),
			[entity.bookmarked_by, user.id]
		);

		const canvasNavBarContent = useMemo(
			() => ({
				left: (
					<NavBarBreadcrumbWrapper>
						<Group spacing="xs" align="center" noWrap>
							<IconButton
								iconName="chevronsRight"
								variant="tertiary"
								tooltip="Close canvas"
								tooltipProps={{
									position: 'bottom',
									withinPortal: true,
									zIndex: getDefaultZIndex('modal'),
								}}
								onClick={() => {
									onCloseCanvas?.();
								}}
								size="md"
							/>
							<Text
								size="sm"
								weight="semibold"
								color="text/primary/default"
								lineClamp={1}
							>
								{entity.title ?? 'Untitled BI Dashboard'}
							</Text>
						</Group>
					</NavBarBreadcrumbWrapper>
				),
				right: (
					<NavBarRightSideWrapper>
						{entity.teams.length > 0 ? (
							<Button variant="default" onClick={handleGoToDashboard}>
								Go to dashboard
							</Button>
						) : (
							<Button variant="primary" onClick={handleSaveDashboard}>
								Save dashboard
							</Button>
						)}
					</NavBarRightSideWrapper>
				),
			}),
			[
				entity.title,
				entity.teams.length,
				handleGoToDashboard,
				handleSaveDashboard,
				onCloseCanvas,
			]
		);

		const pageNavBarContent = useMemo(
			() => ({
				left: (
					<NavBarBreadcrumbWrapper>
						<Breadcrumbs
							crumbs={breadcrumbs ?? []}
							data-testid="bi-dashboard-breadcrumbs"
						/>
					</NavBarBreadcrumbWrapper>
				),
				right: (
					<NavBarRightSideWrapper>
						{hasManagePermission && (
							<Group spacing="xs">
								<IconButton
									variant="tertiary"
									onClick={onAddMetric}
									iconName="plus"
									tooltip="Add metric..."
								/>
							</Group>
						)}
						<IconButton
							iconName="bookmark"
							variant="tertiary"
							tooltip="Bookmark"
							size="md"
							fillIcon={isBookmarked}
							onClick={() => {
								const bookmarkedBy = isBookmarked
									? entity.bookmarked_by.filter((userId) => userId !== user.id)
									: [...entity.bookmarked_by, user.id];
								handleToggleBookmark(bookmarkedBy);
							}}
						/>
						<BiDashboardActions
							onRefresh={handleRefreshAllMetrics}
							onDelete={handleDeleteDashboard}
							handleCopyToPng={handleCopyAsPng}
							handleDownloadPng={handleDownloadPng}
							hasManagePermission={hasManagePermission}
						/>
					</NavBarRightSideWrapper>
				),
			}),
			[
				breadcrumbs,
				hasManagePermission,
				onAddMetric,
				isBookmarked,
				handleRefreshAllMetrics,
				handleDeleteDashboard,
				handleCopyAsPng,
				handleDownloadPng,
				entity.bookmarked_by,
				user.id,
				handleToggleBookmark,
			]
		);

		const isAISidebarOpen =
			dashboardRightSidebarStore.mode === 'ai' &&
			!dashboardRightSidebarStore.collapsed;
		const isInfoSidebarOpen =
			dashboardRightSidebarStore.mode === 'info' &&
			!dashboardRightSidebarStore.collapsed;

		const sidesheetControls = useMemo(
			() => (
				<NavBarSidesheetControlsWrapper>
					<IconButton
						iconName={isAISidebarOpen ? 'sparklesFilled' : 'sparkles'}
						variant="tertiary"
						highlight={isAISidebarOpen}
						tooltip={isAISidebarOpen ? 'Hide AI sidebar' : 'Show AI sidebar'}
						onClick={() => handleToggleRightSidebar('ai')}
					/>
					<IconButton
						iconName={isInfoSidebarOpen ? 'infoCircleFilled' : 'infoCircle'}
						variant="tertiary"
						tooltip={isInfoSidebarOpen ? 'Hide details' : 'Show details'}
						onClick={() => handleToggleRightSidebar('info')}
					/>
				</NavBarSidesheetControlsWrapper>
			),
			[handleToggleRightSidebar, isAISidebarOpen, isInfoSidebarOpen]
		);

		const navBarContent =
			mode === 'canvas' ? canvasNavBarContent : pageNavBarContent;

		return (
			<NavBarWrapper>
				{navBarContent.left}
				{navBarContent.right}
				{mode === 'page' && sidesheetControls}
			</NavBarWrapper>
		);
	}
);
